import { ChangeEventHandler, FC, MouseEventHandler, ReactNode } from 'react';

import { Theme, useMediaQuery } from '@mui/material';
import Box from '@o2/ui/Box';
import Button from '@o2/ui/Button';
import Paper, { defaultPaperPaddings, PaperProps } from '@o2/ui/Paper';
import Stack from '@o2/ui/Stack';
import TextField from '@o2/ui/TextField';

import Item from './Item';

export const fileUploadWidgetTemplateId = '10.6.7';

export interface FileUploadWidgetProps extends Omit<PaperProps, 'title'> {
  /**
   * Allow a read-only view. If true and `!readOnly`, an extra 'Cancel' button will be shown to (fire a callback to) return
   * to the read-only view (`onToggleReadOnly`).
   */
  allowReadOnly?: boolean;

  description?: ReactNode;

  /**
   * Label of the document type Select.
   */
  documentTypeLabel?: ReactNode;

  /**
   * Options of the document type Select.
   *
   * Please use an array, not a Fragment: https://github.com/mui/material-ui/issues/16181#issuecomment-1024944788
   */
  documentTypeOptions?: ReactNode;

  /**
   * Label of the file source Select.
   */
  fileSourceLabel?: ReactNode;

  /**
   * Options of the file source Select.
   *
   * Please use an array, not a Fragment: https://github.com/mui/material-ui/issues/16181#issuecomment-1024944788
   */
  fileSourceOptions?: ReactNode;

  /**
   * Text to be shown in the button that switches TO read-only mode (= shown when the component IS NOT currently read-only).
   */
  labelReadOnlyEnter?: ReactNode;
  /**
   * Text to be shown in the button that switches FROM read-only mode (= shown when the component IS currently read-only).
   */
  labelReadOnlyLeave?: ReactNode;

  onChangeDocumentType?: ChangeEventHandler<HTMLInputElement>;

  onChangeFileSource?: ChangeEventHandler<HTMLInputElement>;

  /**
   * Fired when entering or leaving read-only mode.
   */
  onToggleReadOnly?: MouseEventHandler;

  /**
   * When true, only display files already uploaded AND A BUTTON to quit the read-only view (`onToggleReadOnly`).
   * The `allowReadOnly` prop has to be `true` for this prop to have any effect. If you only ever want to display
   * already uploaded files with no option to upload new ones, use `FileList` instead of this component!
   */
  readOnly?: boolean;

  /**
   * `<FileUploadButton />` will usually be used here, unless the file source is DMS or another non-upload source.
   */
  selectButton: ReactNode;

  /**
   * Value of the document type Select.
   */
  selectedDocumentType?: unknown;

  /**
   * Value of the file source Select.
   */
  selectedFileSource?: unknown;

  title?: ReactNode;
}

type FileUploadWidget = FC<FileUploadWidgetProps> & {
  Item: typeof Item;
};

/**
 * Template: 10.6.7 - Nahrávání souborů
 */
const FileUploadWidget: FileUploadWidget = ({
  allowReadOnly,
  children,
  description,
  documentTypeLabel = 'Typ dokumentu',
  documentTypeOptions,
  fileSourceLabel = 'Zdroj souboru',
  fileSourceOptions,
  labelReadOnlyEnter = 'Zrušit',
  labelReadOnlyLeave = 'Přidat přílohu',
  onChangeDocumentType,
  onChangeFileSource,
  onToggleReadOnly,
  readOnly,
  selectButton,
  selectedDocumentType,
  selectedFileSource,
  title,
  ...other
}) => {
  const xs = useMediaQuery((theme: Theme) => theme.breakpoints.only('xs'));

  return (
    <Paper p={title || description || children ? defaultPaperPaddings : 0} {...other}>
      <Stack spacing={4}>
        {(title || description) && (
          <Stack spacing={0.5}>
            {title && <Box data-elm={`${fileUploadWidgetTemplateId}_001`}>{title}</Box>}
            {description && <Box data-elm={`${fileUploadWidgetTemplateId}_002`}>{description}</Box>}
          </Stack>
        )}
        <Stack spacing={1}>{children}</Stack>
      </Stack>
      <Paper.Footer
        px={defaultPaperPaddings}
        py={2}
        sx={(theme) => ({
          background:
            other.color && other.color !== 'transparent' ? theme.palette.background[other.color] : 'transparent',
        })}
      >
        {(!allowReadOnly || !readOnly) && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {fileSourceOptions && (
              <TextField
                data-elm={`${fileUploadWidgetTemplateId}_004`}
                select
                fullWidth={xs}
                onChange={onChangeFileSource}
                value={selectedFileSource}
                label={fileSourceLabel}
              >
                {fileSourceOptions}
              </TextField>
            )}
            {documentTypeOptions && (
              <TextField
                data-elm={`${fileUploadWidgetTemplateId}_005`}
                select
                fullWidth={xs}
                onChange={onChangeDocumentType}
                value={selectedDocumentType}
                label={documentTypeLabel}
              >
                {documentTypeOptions}
              </TextField>
            )}
            <Box data-elm={`${fileUploadWidgetTemplateId}_006, ${fileUploadWidgetTemplateId}_007`} flexGrow={1}>
              {selectButton}
            </Box>
            {allowReadOnly && (
              <Button onClick={onToggleReadOnly} fullWidth={xs}>
                {labelReadOnlyEnter}
              </Button>
            )}
          </Stack>
        )}
        {allowReadOnly && readOnly && (
          <Button onClick={onToggleReadOnly} fullWidth={xs}>
            {labelReadOnlyLeave}
          </Button>
        )}
      </Paper.Footer>
    </Paper>
  );
};

FileUploadWidget.Item = Item;

export default FileUploadWidget;
